import { fabric } from 'fabric';
import _ from 'lodash';

import { DEFAULT_FONT, DEFAULT_FONT_BOLD } from '@/shared/ui';
import { convertToMomentJs } from '@/shared/lib/aiUtils';

type TitleChartCtor = new (
  canvas: fabric.Canvas,
  verticalChannelPlacement: number[],
  config: any,
  displayedChannelsArr: number[],
  timeObject: { startMoment: any; timezoneOffset: number },
) => any;

let TitleChartImpl: any;

(function () {
  TitleChartImpl = function (
    canvas: fabric.Canvas,
    verticalChannelPlacement: number[],
    config: any,
    displayedChannelsArr: number[],
    timeObject: { startMoment: any; timezoneOffset: number },
  ) {
    this.canvas = canvas;
    this.verticalChannelPlacement = verticalChannelPlacement;
    this.displayedChannelsArr = displayedChannelsArr;
    this.config = config;
    this.textTime = null;
    this.textTitle = null;
    this.textChannel = [];
    this.timeObject = timeObject;
    this.isEcgViewerTab = !_.isEmpty(this.timeObject);
    this.titleFont = {
      fill: '#1D2F47',
      fontFamily: DEFAULT_FONT,
      selectable: false,
      hasControls: false,
      hasBorders: false,
      hasRotatingPoint: false,
      selection: false,
      skipTargetFind: false,
      editable: false,
    };
  };

  TitleChartImpl.prototype.update = function (
    verticalChannelPlacement: number[],
    config: any,
    displayedChannelsArr: number[],
    timeObject: any,
  ) {
    this.verticalChannelPlacement = verticalChannelPlacement;
    this.displayedChannelsArr = displayedChannelsArr;
    this.config = config;
    this.timeObject = timeObject;
  };

  TitleChartImpl.prototype.getTime = function (offsetX: number) {
    const { startMoment, timezoneOffset } = this.timeObject;
    const { secondsToPix, indexToPix } = this.config;
    const addedTimezoneStartMoment = convertToMomentJs(startMoment, timezoneOffset);
    this.currentTime = (offsetX * indexToPix) / secondsToPix;
    const currentStartTimeMoment = addedTimezoneStartMoment
      .clone()
      .add(this.currentTime, 'seconds');
    return currentStartTimeMoment;
  };

  TitleChartImpl.prototype.draw = function (offsetX: number) {
    const { secondsToPix, indexToPix, recDuration } = this.config;

    let offsetStr = '';

    if (this.isEcgViewerTab) {
      // *: New UI
      const { startMoment, timezoneOffset } = this.timeObject;
      const addedTimezoneStartMoment = convertToMomentJs(startMoment, timezoneOffset);
      this.currentTime = (offsetX * indexToPix) / secondsToPix;
      const currentStartTimeMoment = addedTimezoneStartMoment
        .clone()
        .add(this.currentTime, 'seconds');
      const currentStartTimeStr = currentStartTimeMoment.format('HH:mm:ss');
      const currentStopTimeStr = currentStartTimeMoment
        .clone()
        .add(this.config.wndDuration, 'seconds')
        .format('HH:mm:ss');
      offsetStr = `${currentStartTimeMoment.format('MM/DD/YYYY')} ${currentStartTimeStr} - ${currentStopTimeStr}`;
    } else {
      this.currentTime = (offsetX * indexToPix) / secondsToPix;
      this.totalTime = recDuration;

      // *: For test purpose (3 cases)
      // this.currentTime = 3700;
      // this.totalTime = 4000;

      const currentTimeStr = this.getTimeInfo(this.currentTime);
      const totalTimeStr = this.getTimeInfo(this.totalTime);
      offsetStr = `${currentTimeStr} / ${totalTimeStr}`;
    }

    this.drawTime(offsetStr);

    const objectTitle = this.getTitle();
    this.drawTitle(objectTitle);

    this.drawChannel();
  };

  TitleChartImpl.prototype.drawTitle = function (
    objectTitle: { title?: string; filterText?: string } = {},
  ) {
    const { width, height } = this.config;
    const { title, filterText } = objectTitle;
    const formatText = `${title}${filterText ? ` - ${filterText}` : ''}`;
    if (!this.textTitle) {
      this.textTitle = new fabric.IText(formatText, {
        ...this.titleFont,
        fontWeight: 400,
        fontSize: 16,
        left: 5,
        top: height - 24,
        hoverCursor: 'arrow',
      });
      this.canvas.add(this.textTitle);
    } else {
      this.textTitle.set({
        text: formatText,
        left: 5,
        top: height - 24,
        fontSize: 16,
      });
    }
  };

  TitleChartImpl.prototype.drawChannel = function () {
    const { majorDivSize, channelLabels, someChannelsHidden, speed } = this.config;
    let vertOffset = Math.min.apply(null, this.verticalChannelPlacement) / 2;
    vertOffset = Math.min(vertOffset, majorDivSize / 2);
    for (let j = 0; j < channelLabels.length; j += 1) {
      if (j < this.textChannel.length) {
        this.textChannel[j].set({
          text: channelLabels[j],
          left: 5,
          // *: Translate
          top:
            speed >= 25
              ? this.verticalChannelPlacement[j] - vertOffset
              : speed === 10
                ? this.verticalChannelPlacement[j] - vertOffset - 10
                : this.verticalChannelPlacement[j] - vertOffset - 25,
          visible: true,
          fontSize: 16,
        });
        this.textChannel[j].setCoords();
      } else {
        const channel = new fabric.IText(channelLabels[j], {
          ...this.titleFont,
          fontSize: 16,
          fontWeight: 700,
          fill: '#828C99',
          left: 5,
          top:
            speed >= 25
              ? this.verticalChannelPlacement[j] - vertOffset
              : speed === 10
                ? this.verticalChannelPlacement[j] - vertOffset - 10
                : this.verticalChannelPlacement[j] - vertOffset - 25,
          hoverCursor: 'arrow',
          fontFamily: DEFAULT_FONT_BOLD,
        });
        this.textChannel.push(channel);
        this.canvas.add(channel);
      }
    }

    // *: Hide channel label
    if (someChannelsHidden) {
      for (let k = channelLabels.length; k < this.textChannel.length; k += 1) {
        this.textChannel[k].set({ visible: false });
      }
    }
  };

  TitleChartImpl.prototype.drawTime = function (text: string) {
    const { height, width } = this.config;
    let widthTextTime =
      this.currentTime >= 3600 && this.totalTime >= 3600
        ? 220
        : this.currentTime < 3600 && this.totalTime >= 3600
          ? 200
          : 180;
    widthTextTime += 62;
    if (!this.textTime) {
      this.textTime = new fabric.IText(text, {
        ...this.titleFont,
        fontSize: 16,
        fontWeight: 400,
        width: widthTextTime,
        hoverCursor: 'arrow',
        left: width - widthTextTime - 5,
        top: height - 24,
      });
      this.canvas.add(this.textTime);
    } else {
      this.textTime.set({
        text,
        width: widthTextTime,
        fontSize: 16,
        left: width - widthTextTime - 5,
        top: height - 24,
      });
    }
  };

  TitleChartImpl.prototype.getTimeInfo = function (timeSeconds: number) {
    const totalSeconds = timeSeconds;
    let remainSeconds = totalSeconds;

    // *: Get values to hours, minutes, seconds, milliseconds
    const hours = Math.floor(remainSeconds / 3600);
    remainSeconds -= hours * 3600;
    const minutes = Math.floor(remainSeconds / 60);
    remainSeconds -= minutes * 60;
    const seconds = Math.floor(remainSeconds);
    const milliSeconds = Math.floor((remainSeconds - seconds) * 1000);

    // *: Append '0' to the front of values if necessary
    const hoursText = `0${hours}`.slice(-2);
    const minutesText = `0${minutes}`.slice(-2);
    const secondsText = `0${seconds}`.slice(-2);
    const millisecondsText = `00${milliSeconds}`.slice(-3);

    let offsetText = '';
    if (totalSeconds >= 3600 || this.isEcgViewerTab) {
      offsetText += `${hoursText}:`;
    }
    offsetText += `${minutesText}:${secondsText}.${millisecondsText}`;

    return offsetText;
  };

  TitleChartImpl.prototype.getTitle = function () {
    const { LFF, HFF, researchMode, hffOrder, bccHFF, bccMinSR, speed, gain } = this.config;
    let filterText = '';

    if (LFF > 0) {
      if (HFF > 0) {
        filterText += `HP: ${LFF.toString()}Hz, `;
      } else {
        filterText += `HP: ${LFF.toString()}Hz`;
      }
    }

    if (HFF > 0) {
      if (HFF === 50 || HFF === 60) {
        filterText += `LP: ${HFF}Hz - Notch`;
      } else {
        filterText += `LP: ${HFF.toString()}Hz`;
      }

      if (researchMode) {
        filterText += ` [N=${hffOrder}] [Nb=${bccHFF}][minSR=${bccMinSR}]`;
      }
    }

    // *: Round speed value
    const title = `${speed.toFixed(1).toString()}mm/s, ${gain.toString()}mm/mV`;

    return { title, filterText };
  };

  TitleChartImpl.prototype.updateUploadedFile = function () {
    _.forEach(this.textChannel, (channel) => {
      this.canvas.remove(channel);
    });

    this.canvas.remove(this.textTitle);

    this.textChannel.length = 0;
    this.textTitle = null;
  };

  TitleChartImpl.prototype.removeLabel = function () {
    if (this.cornerTitle) {
      this.canvas.remove(this.cornerTitle);
      this.cornerTitle = null;
    }
  };

  TitleChartImpl.prototype.showLabel = function (text: string) {
    const { width, height } = this.config;
    const left = width - 35;
    this.cornerTitle = new fabric.IText(`${text}`, {
      ...this.titleFont,
      fill: 'red',
      fontWeight: 700,
      fontSize: 16,
      left,
      top: height - 50,
      textAlign: 'justify-center',
      originX: 'center',
      hoverCursor: 'arrow',
    });
    this.canvas.add(this.cornerTitle);
  };

  TitleChartImpl.prototype.toggleDelLabel = function (beatTypeNumber: number) {
    const { width, height } = this.config;
    this.isInDelMode = !this.isInDelMode;
    if (this.isInDelMode) {
      const text =
        beatTypeNumber === 1
          ? 'MULTI N'
          : beatTypeNumber === 60
            ? 'MULTI V'
            : beatTypeNumber === 70
              ? 'MULTI S'
              : beatTypeNumber === 80
                ? 'MULTI Q'
                : beatTypeNumber === 90
                  ? 'MULTI IVCD'
                  : 'DEL';
      // *: Change position temporarily when drawing AI beats
      // const left = !beatTypeNumber
      //   ? this.textTitle2.width + 35
      //   : beatTypeNumber === 90
      //     ? this.textTitle2.width + 65
      //     : this.textTitle2.width + 55;
      const left = !beatTypeNumber ? width - 25 : beatTypeNumber === 90 ? width - 55 : width - 42;
      this.delTitle = new fabric.IText(text, {
        ...this.titleFont,
        fill: 'red',
        fontWeight: 700,
        fontSize: 16,
        left,
        // *: Change position temporarily when drawing AI beats
        // top: height - 20,
        top: height - 50,
        textAlign: 'justify-center',
        originX: 'center',
        hoverCursor: 'arrow',
      });
      this.canvas.add(this.delTitle);
    } else {
      this.canvas.remove(this.delTitle);
      this.delTitle = null;
    }
  };

  TitleChartImpl.prototype.updateData = function (config: any, timeObject: any) {
    this.isInDelMode = false;
    this.canvas.remove(this.delTitle);
    this.delTitle = null;
    this.removeLabel();
    this.config = config;
    this.timeObject = timeObject;
  };
})();

const TitleChart: TitleChartCtor = TitleChartImpl;

export { TitleChart };
export default { TitleChart };
